import React, { useState } from 'react';
import get from 'lodash/get';

// Components
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpButton from 'Wpds/components/button/WpButton';
import SignupCheckEmail from '../Onboarding/SignupCheckEmail/SignupCheckEmail';
import { WpAlert } from 'Wpds/components/alert';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Apis
import * as accountManagementApi from 'Shared/api/AccountManagement.api';

// Styles
import './SignUpPIForm.scss';

type Props = {
  inviterSlug?: string;
  workspaceSlug?: string;
  currentEmail?: string;
  invitationToken?: string;
};

const SignUpPIForm = ({ inviterSlug, workspaceSlug, currentEmail, invitationToken }: Props) => {
  const csrfValue = get(document.getElementsByName('csrf-token')[0], 'content');
  const [isSubmittingPIForm, toggleIsSubmittingPIForm] = useToggle(false);
  const [isSubmittedPIFormSuccess, toggleisSubmittedPIFormSuccess] = useToggle(false);
  const [error, setError] = useState(null);
  let errorMessage = '';

  const handlePISubmit = event => {
    toggleIsSubmittingPIForm();
    // Create PI Account
    const firstName = event.user.firstname;
    const lastName = event.user.lastname;
    accountManagementApi
      .createPIAccount({
        first_name: firstName,
        last_name: lastName,
        invitation_token: invitationToken,
      })
      .then(() => {
        toggleisSubmittedPIFormSuccess();
        setError(null);
      })
      .catch(error => {
        // Server responded with a non-2xx status
        if (error.response) {
          errorMessage = error?.response.data.error;
          setError(errorMessage);
        }
        toggleIsSubmittingPIForm();
      });
  };

  const baseClassName = 'SignUpPIForm';

  return (
    <WpContainer className={baseClassName}>
      <WpContainerBody>
        {isSubmittedPIFormSuccess ? (
          <SignupCheckEmail customMessage="We have sent you an email to create your PI Account" hideResend={true} />
        ) : (
          <>
            <p className={`${baseClassName}__title`}>Sign up with email</p>
            <WpForm onSubmit={handlePISubmit}>
              <WpTextField
                id="authenticity_token"
                name="authenticity_token"
                label="authenticity_token"
                type="hidden"
                value={csrfValue}
              />
              <WpFormLayout spacing="md">
                <WpTextField
                  name="firstname"
                  id="user[firstname]"
                  type="text"
                  label="First name"
                  placeholder=""
                  required
                  showRequiredIndicator={true}
                  disabled={isSubmittingPIForm}
                  autoFocus
                />
                <WpTextField
                  name="lastname"
                  id="user[lastname]"
                  type="text"
                  label="Last name"
                  placeholder=""
                  required
                  showRequiredIndicator={true}
                  disabled={isSubmittingPIForm}
                />
                <WpTextField
                  name="email"
                  id="user[email]"
                  type="text"
                  label="Work email"
                  placeholder=""
                  required
                  value={currentEmail}
                  showRequiredIndicator={false}
                  disabled={!!currentEmail || isSubmittingPIForm}
                />
              </WpFormLayout>
              {inviterSlug && (
                <WpTextField
                  label="inviter_slug"
                  id="inviter_slug"
                  name="inviter_slug"
                  value={inviterSlug}
                  readOnly
                  type="hidden"
                />
              )}
              {workspaceSlug && (
                <WpTextField
                  label="workspace_slug"
                  id="workspace_slug"
                  name="workspace_slug"
                  value={workspaceSlug}
                  readOnly
                  type="hidden"
                />
              )}
              {invitationToken && (
                <WpTextField
                  label="user[invitation_token]"
                  id="user[invitation_token]"
                  name="user[invitation_token]"
                  value={invitationToken}
                  type="hidden"
                />
              )}
              {invitationToken && <WpTextField label="_method" id="_method" name="_method" value="put" type="hidden" />}
              {error && (
                <WpFormLayout>
                  <WpAlert type="danger">{error}</WpAlert>
                </WpFormLayout>
              )}
              <WpButton size="lg" btnType="submit" block loading={isSubmittingPIForm}>
                Sign up with email
              </WpButton>
            </WpForm>
          </>
        )}
      </WpContainerBody>
    </WpContainer>
  );
};

export default SignUpPIForm;
